import React, { useCallback, useEffect } from 'react';
import {
  useDeviceCodeAuth,
  DeviceCodeProgress,
} from '@/libs/device-auth/hooks/useDeviceCodeAuth';
import { LoadingSpinner } from '@/components/loading/Loading';
import styles from './LandingScreen.module.css';
import QRCode from 'react-qr-code';
import ThreeDotLoader from '@/components/three-dots-loading/ThreeDotLoader';
import { DevicePairingCard, Logo } from '@oolio-group/design-ui-react';

export const LandingScreen: React.FC = () => {
  const {
    initiateDeviceCodeFlow,
    cancelPairing,
    userCode,
    qrCodeUrl,
    timeRemaining,
    status: deviceCodeStatus,
  } = useDeviceCodeAuth();

  useEffect(() => {
    // Automatically start device code flow on screen mount
    initiateDeviceCodeFlow();

    return () => {
      cancelPairing();
    };
  }, [initiateDeviceCodeFlow, cancelPairing]);

  const onRetry = useCallback(() => {
    initiateDeviceCodeFlow({ retry: true });
  }, [initiateDeviceCodeFlow]);

  const renderContent = () => {
    switch (deviceCodeStatus) {
      case DeviceCodeProgress.IDLE:
      case DeviceCodeProgress.DEVICE_CODE_REQUESTED:
        return (
          <div className={styles.initializing}>
            <LoadingSpinner />
            <p className={styles.statusText}>Initializing device pairing...</p>
          </div>
        );

      case DeviceCodeProgress.DEVICE_CODE_RECEIVED:
      case DeviceCodeProgress.TOKEN_REQUESTED:
      case DeviceCodeProgress.DEVICE_CODE_EXPIRED:
      case DeviceCodeProgress.CANCELLED:
        return (
          <DevicePairingCard
            code={userCode}
            onRefreshCode={onRetry}
            expired={
              qrCodeUrl
                ? deviceCodeStatus === DeviceCodeProgress.DEVICE_CODE_EXPIRED ||
                  deviceCodeStatus === DeviceCodeProgress.CANCELLED
                : false
            }
            timerDuration={timeRemaining}
            loading={!qrCodeUrl}
          >
            {qrCodeUrl && (
              <div className={styles.qrContainer}>
                <QRCode value={qrCodeUrl} size={180} width={180} />
              </div>
            )}
          </DevicePairingCard>
        );

      case DeviceCodeProgress.TOKEN_RECEIVED:
        return (
          <div className={styles.pairingSuccess}>
            <h2 className={styles.successTitle}>✅ Authenticated!</h2>
            <p className={styles.statusText}>
              Redirecting <ThreeDotLoader />
            </p>
          </div>
        );

      default:
        return (
          <div className={styles.initializing}>
            <LoadingSpinner />
            <p className={styles.statusText}>Something not working...</p>
          </div>
        );
    }
  };

  return (
    <div className={styles.landingScreen}>
      <div className={styles.logoContainer}>
        <Logo
          brand="oolio"
          color="light"
          height={44}
          type="brandmark"
          width={44}
        />
      </div>
      {renderContent()}
    </div>
  );
};
